import { Component, ElementRef, OnDestroy, Input, Output, OnInit, EventEmitter, ViewEncapsulation } from '@angular/core';
import * as moment from 'moment/moment';

import {
  D3Service,
  D3,
  Axis,
  BrushBehavior,
  BrushSelection,
  D3BrushEvent,
  ScaleLinear,
  ScaleOrdinal,
  Selection,
  Transition,
  DSV
} from 'd3-ng2-service';

@Component({
  selector: 'latitude-ui-histogram',
  templateUrl: './histogram.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./histogram.component.scss']
})
export class LatitudeUIHistogramComponent implements OnInit {

  private d3: D3;

  @Input() parentRef;
  @Input() valuesFormatter: any = false;
  @Input() modelStart: any;
  @Input() modelEnd: any;
  @Input() dateOnTooltip = false;
  @Input() tooltipValueUnits = '';
  @Input() tooltipDecimals = 2;
  @Input() tooltipCompactNumber = false;
  @Input() brushFill: string;

  @Output() dateChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() noData: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() totalDays: EventEmitter<number> = new EventEmitter<number>();

  timeFilter = 'month';
  timeFilterNumber: number;

  timeOnTooltip = false;
  tooltipDiv: any;

  svg: any;
  brush: any;
  brushing = false;
  transition: any;

  bars: any;
  x: any;
  y: any;

  x2: any;

  tTimeout: any;
  tDuration = 350;

  private margin: any;
  private width: any;
  private height: any;

  noDataFound: boolean;

  private minDateLimit;
  private maxDateLimit;
  private originalModelStart;
  private originalModelEnd;

  constructor(d3Service: D3Service) {
    this.d3 = d3Service.getD3();
  }

  // @TODO: translate
  private getD3Formatters() {
    return {
      'decimal': '.',
      'thousands': ',',
      'grouping': [3],
      'currency': ['$', ''],
      'dateTime': '%a %b %e %X %Y',
      'date': '%m/%d/%Y',
      'time': '%H:%M:%S',
      'periods': ['AM', 'PM'],
      'days': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      'shortDays': ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      'months': ['January', 'Feb', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'Dicember'],
      'shortMonths': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dic']
    };
  }

  ngOnInit() {
    this.minDateLimit = this.modelStart ? this.modelStart.clone() : null;
    this.maxDateLimit = this.modelEnd ? this.modelEnd.clone() : null;
    this.originalModelStart = this.modelStart;
    this.originalModelEnd = this.modelEnd;
  }

  refreshOnlyData(data) {
    let barCounts = 0;
    data.forEach((d) => {
      d.date = moment(d.time);
      if (d.value > 0) {
        barCounts++;
      }
    });

    if (!barCounts) {
      this.dataInformationEvent(true);
    } else {
      this.dataInformationEvent(false);
    }

    this.svg.selectAll('rect').remove();
    const startDate = this.modelStart.startOf('d');
    const finishDate = this.modelEnd.endOf('d');

    const difference = finishDate.diff(startDate, 'days');
    const histogram = this.getHistogramMethod(difference, this.x);

    this.setBrush();

    const bins = histogram(data);

    this.y.domain([0, this.d3.max(bins, (d: any) => {
      return d.length > 0 ? d[0].value : 0;
    })]);


    this.appendBars(bins);

    const brushContainer = this.svg.append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .selectAll('rect')
          .attr('height', this.height);

    this.barsTransition();
    this.brushEvents(brushContainer);
  }

  refresh(data) {
    if (!data || !data.length) {
      return;
    }
    const self = this;
    // Set D3 default format locale options
    this.d3.timeFormatDefaultLocale(<any>this.getD3Formatters());
    // Set the histogram width, height and margins based on the screen size
    this.setHistogramSizes();

    let barCounts = 0;
    data.forEach((d) => {
      d.date = moment(d.time);
      if (d.value > 0) {
        barCounts++;
      }
    });
    if (!barCounts) {
      this.dataInformationEvent(true);
      this.noDataFound = true;
    } else {
      this.dataInformationEvent(false);
      this.noDataFound = false;
    }

    // Parse dates
    const parseDate = this.d3.timeParse('%d-%m-%Y');
    const startDate = this.modelStart ? this.modelStart.startOf('d') : moment();
    const finishDate = this.modelEnd ? this.modelEnd.endOf('d') : moment().add(10, 'days');

    // Set the ranges
    this.x = this.d3.scaleTime()
        .domain([new Date(startDate.format()), new Date(finishDate.format())])
        .rangeRound([0, this.width]);
    this.y = this.d3.scaleLinear()
        .range([this.height, 0]);

    const difference = finishDate.diff(startDate, 'days');
    const histogram = this.getHistogramMethod(difference, this.x);

    // Emit event with the total selected days so that the timeslider.component can display it
    this.totalDaysEvent(difference);

    // Set SVG and base properties
    this.d3.select('div#histogram').html('');

    this.svg = this.d3.select('div#histogram').append('svg')
        .attr('width', this.width + this.margin.left + this.margin.right)
        .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
        .attr('class', 'barsContainer')
        .attr('transform',
              'translate(' + this.margin.left + ',' + this.margin.top + ')');

        this.d3.select('div#histogram').append('div').attr('class', 'loadingWrapper');
        this.d3.select('.loadingWrapper').append('div').attr('class', 'loadingBar');
        this.d3.select('.loadingWrapper').append('div').attr('class', 'loadingText');

    // Define xAxis & yAxis
    let xAxis = this.d3.axisBottom(this.x);
    const yAxis = this.d3.axisRight(this.y);

    // Format axis labels
    // if (this.timeFilter === 'h' && this.timeFilterNumber <= 24 && (difference + 1) >= 8) {
    //   // When the time is going to be formatted in hours, let's show DayOfWeek + DayOfMonth
    //   xAxis = xAxis.tickFormat(this.d3.timeFormat('%a %e'));
    // } else if (this.timeFilter === 'h' && this.timeFilterNumber <= 24 && (difference + 1) <= 7) {
    //   this.timeOnTooltip = true;
    //   xAxis = xAxis.tickFormat(this.d3.timeFormat('%a%e - %H:00'));
    // }

    xAxis = xAxis.tickFormat(this.d3.timeFormat('%m/%d'));

    // Define brush & its events
    this.setBrush();
    // Group the data for the bars
    const bins = histogram(data);
    // Scale the range of the data in the y domain
    this.y.domain([0, this.d3.max(bins, (d: any) => {
      return d.length > 0 ? d[0].value : 0;
    })]);

    this.transition = this.svg.transition().duration(this.tDuration);
    this.svg.append('g')
      .attr('class', 'grid')
      .call(this.makeYGridlines()
          .tickSize(-this.width)
          .tickFormat(<any>'')
      );
    this.appendBars(bins);

    const brushContainer = this.svg.append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .selectAll('rect')
          .attr('height', this.height);

    this.barsTransition();

    this.svg.append('g')
      .attr('transform', `translate(-${this.width}, ${this.height})`)
      .transition(this.transition)
      .attr('transform', `translate(0, ${this.height})`)
      .attr('class', 'axis axis--x')
      .call(xAxis);

      this.brushEvents(brushContainer);
  }

  private setBrush() {
    this.brush = this.d3.brushX()
      .extent([[0, 0], [this.width, this.height]]);

    // Add the selected class to the bars that are brushed
    this.brush.on('brush', () => {
      this.brushing = true;
      this.d3.select('.barsContainer').attr('class', 'barsContainer greyBars');
      this.d3.select('.brush .selection').attr('fill', this.brushFill);
      const selection = this.d3.event.selection;
      this.bars.classed('selected', (d, index) => {
        if (selection && this.x !== undefined) {
          const barStartsPoint = this.x(d.x0);
          const barEndsPoint = this.x(d.x0) + (this.x(d.x1) - this.x(d.x0) - 1);
          const condition_1_1 = selection[0] <= barStartsPoint;
          const condition_1_2 = selection[0] > barStartsPoint && selection[0] < barEndsPoint;
          const condition_2 = barStartsPoint <= selection[1];
          const isBrushed = (condition_1_1 || condition_1_2) && condition_2;
          return isBrushed;
        } else {
          return false;
        }
      });
    });

    // Get the selected date and change the x.domain accordingly
    this.brush.on('end', () => {
      this.brushing = false;
      this.d3.select('.barsContainer').attr('class', 'barsContainer');
      this.d3.select('body').selectAll('div.tooltip').remove();

      const s = this.d3.event.selection;
      if (!s) {
        if (!this.tTimeout) {
          return this.tTimeout = setTimeout(() => {
            this.tTimeout = null;
          }, this.tDuration);
        }
        this.x.domain(this.x);
      } else {
        this.x.domain([s[0], s[1]].map(this.x.invert, this.x));
        this.svg.select('.brush').call(this.brush.move, null);
      }
      const first = moment(this.x.domain()[0]).format('DD.MM.YYYY');
      const last = moment(this.x.domain()[1]).format('DD.MM.YYYY');
      // const firstDate = moment(first, 'DD.MM.YYYY');
      // const lastDate = moment(last, 'DD.MM.YYYY');
      const firstDate = moment(first, 'DD.MM.YYYY').set({
        hour: this.originalModelStart.format('HH'),
        minute: this.originalModelStart.format('mm'),
        second: 0,
        millisecond: 0
      });
      const lastDate = moment(last, 'DD.MM.YYYY').set({
        hour: this.originalModelEnd.format('HH'),
        minute: this.originalModelEnd.format('mm'),
        second: 0,
        millisecond: 0
      });
      if (lastDate.diff(firstDate, 'h') > 1) {
        // Emit event with the new start and end date in order to:
        //  - Daterange component get updated
        //  - Timeslider component emit event with the new dates
        this.dateChangeEvent(firstDate, lastDate);
      } else {
        this.clearHistogram();
      }
    });
  }

  private brushEvents(brushContainer: any) {
    const self = this;
    brushContainer.on('mouseover', function() {
      const selection = self.d3.mouse(this);
      if (!this.brushing) {
        self.resetBarsFillColor();
      }

      self.bars.each((d, e) => {
        if (selection) {
          const barStartsPoint = self.x(d.x0);
          const barEndsPoint = self.x(d.x0) + (self.x(d.x1) - self.x(d.x0) - 1);

          const isBrushed = barStartsPoint <= selection[0] && selection[0] <= barEndsPoint;
          let barHeight = self.height;
          if (d[0] !== undefined) {
            barHeight = self.y(d[0].value);
          }

          if (isBrushed && (self.height - barHeight) >= (self.height - selection[1])) {
            const rects = <any>self.d3.selectAll('rect');
            const bar = rects._groups[0][e];
            if (!bar) { return; }
            const new_over_event = <any>new Event('mouseover');
            new_over_event.pageX = self.d3.event.pageX;
            new_over_event.clientX = self.d3.event.clientX;
            new_over_event.pageY = self.d3.event.pageY;
            new_over_event.clientY = self.d3.event.clientY;
            bar.dispatchEvent(new_over_event);
          }
        }
      });
    }).on('mousemove', function() {
      const selection = self.d3.mouse(this);
      let brushed = false;
      if (!this.brushing) {
        self.resetBarsFillColor();
      }
      self.bars.each((d, e) => {
        if (selection) {
          const barStartsPoint = self.x(d.x0);
          const barEndsPoint = self.x(d.x0) + (self.x(d.x1) - self.x(d.x0) - 1);

          const isBrushed = barStartsPoint <= selection[0] && selection[0] <= barEndsPoint;
          let barHeight = self.height;
          if (d[0] !== undefined) {
            barHeight = self.y(d[0].value);
          }

          if (isBrushed && (self.height - barHeight) >= (self.height - selection[1])) {
            const rects = <any>self.d3.selectAll('rect');
            const bar = rects._groups[0][e];
            if (!bar) { return; }
            const new_over_event = <any>new Event('mousemove');
            new_over_event.pageX = self.d3.event.pageX;
            new_over_event.clientX = self.d3.event.clientX;
            new_over_event.pageY = self.d3.event.pageY;
            new_over_event.clientY = self.d3.event.clientY;
            bar.dispatchEvent(new_over_event);
            brushed = true;
          }
        }
      });
      if (!brushed && self.tooltipDiv) {
        self.tooltipDiv.remove();
        self.tooltipDiv = undefined;
      }
    }).on('mouseout', () => {
      if (!self.brushing) {
        self.resetBarsFillColor();
      }
      if (self.tooltipDiv) {
        self.tooltipDiv.remove();
        self.tooltipDiv = undefined;
      }
    });
  }

  private makeYGridlines() {
    return this.d3.axisLeft(this.y).ticks(2);
  }

  private appendBars(bins) {
    const bodyNode = this.d3.select('body').node();
    const self = this;
    this.bars = this.svg.selectAll('rect')
      .data(bins)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('height', 0)
      .attr('width', (d, e) => {
        const barWidth = (this.x(d.x1) - this.x(d.x0) - 1);
        return barWidth >= 0 ? barWidth : 0;
      })
      .attr('transform', (d, e) => {
        return `translate(${this.x(d.x0)}, ${this.height})`;
      })
      .on('mouseover', function(d) {
        self.d3.select(this).attr('class', 'bar highlighted');
        self.d3.select('body').selectAll('div.tooltip').remove();
        // Append tooltip
        const absoluteMousePos = self.d3.mouse(<any>bodyNode);
        self.tooltipDiv = self.generateTooltip(absoluteMousePos, d);
      })
      .on('mousemove', function(d) {
        self.d3.select(this).attr('class', 'bar highlighted');

        // Move tooltip
        const absoluteMousePos = self.d3.mouse(<any>bodyNode);
        if (!self.tooltipDiv) {
          self.tooltipDiv = self.generateTooltip(absoluteMousePos, d);
        } else {
          const barData = self.formatTooltipText(d);

          self.tooltipDiv.html(barData);
          self.tooltipDiv.style('left', (absoluteMousePos[0]) + 'px');
          self.tooltipDiv.style('top', (absoluteMousePos[1] - 30) + 'px');
        }
      });
  }

  private barsTransition() {
    this.bars.transition(this.transition)
      .delay(function (d, i) {
        if (d[0] === undefined) { return 0; }
        return i * 25;
      })
      .attr('height', (d) => {
        let barHeight = this.height;
        if (d[0] !== undefined) {
          barHeight = this.y(d[0].value);
        }
        return this.height - barHeight;
      })
      .attr('transform', (d, i) => {
        let barHighestPoint = 0;
        if (d[0] !== undefined) {
          barHighestPoint = this.y(d[0].value);
        }
        return 'translate(' + this.x(d.x0) + ',' + barHighestPoint + ')';
      });
  }

  private generateTooltip(absoluteMousePos, d) {
    if (!this.brushing) {
      this.resetBarsFillColor();
    }

    this.d3.select('body').selectAll('div.tooltip').remove();

    const tooltipDiv = this.d3.select('body')
                  .append('div')
                  .attr('class', 'tooltip');

    const properties = {
      'left': (absoluteMousePos[0]) + 'px',
      'top': (absoluteMousePos[1] - 30) + 'px',
      'background-color': 'rgba(39,39,46,0.9)',
      'border-radius': '12px',
      'padding': '6px 8px',
      'position': 'absolute',
      'z-index': 1024,
      'color': '#FFFFFF',
      'font-size': '11px',
      'line-height': '12px'
    };
    for (const i in properties) {
      if (properties[i]) {
        tooltipDiv.style(i, properties[i]);
      }
    }

    const barData = this.formatTooltipText(d);
    tooltipDiv.html(<any>barData);

    return tooltipDiv;
  }

  private resetBarsFillColor() {
    const self = this;
    this.d3.selectAll('rect')
      .attr('class', function(r) {
        const currentClasses = self.d3.select(this).attr('class');
        if (r[0] !== undefined) {
          return currentClasses.replace('highlighted', '');
        } else {
          return currentClasses;
        }
      });
  }

  private formatTooltipText(d) {
    let barData = d[0].value + ' ' + this.tooltipValueUnits;

    if (this.valuesFormatter) {
      // value + units
      barData = this.valuesFormatter(d[0].value, this.tooltipValueUnits, this.tooltipDecimals, this.tooltipCompactNumber);
    }

    if (this.timeOnTooltip) {
      barData = d[0].date.format('DD/MM HH:mm') + ' - ' + barData;
    } else if (this.dateOnTooltip) {
      barData = d[0].date.format('DD/MM/YYYY') + ' - ' + barData;
    }

    return '<p>' + barData + '</p>';
  }

  private setHistogramSizes() {
    const contentWidth = this.parentRef.getBoundingClientRect().width;
    this.margin = {top: 27, right: 48, bottom: 29, left: 40};
    this.width =  contentWidth - this.margin.left - this.margin.right;
    this.height = 124 - this.margin.top - this.margin.bottom;
  }

  private getHistogramMethod(difference, x) {
    this.setHistogramTimeFrame(difference);

    const startDate = this.modelStart.startOf('d');
    const finishDate = this.modelEnd.endOf('d');
    if (this.timeFilter === 'month') {
      return this.d3.histogram()
        .value((d: any) => { return d.date; })
        .domain(this.x.domain())
        .thresholds(<any>this.d3.timeMonth.range(
          <any>new Date(startDate.format()),
          <any>new Date(finishDate.format()),
          <any>this.timeFilterNumber > 0 ? this.timeFilterNumber : 1
        ));
    } else if (this.timeFilter === '7d') {
      return this.d3.histogram()
        .value((d: any) => { return d.date; })
        .domain(this.x.domain())
        .thresholds(<any>this.d3.timeWeek.range(
          <any>new Date(startDate.format()),
          <any>new Date(finishDate.format()),
          <any>this.timeFilterNumber > 0 ? this.timeFilterNumber : 1
        ));
    } else if (this.timeFilter === 'd') {
      return this.d3.histogram()
        .value((d: any) => { return d.date; })
        .domain(this.x.domain())
        .thresholds(<any>this.d3.timeDay.range(
          <any>new Date(startDate.format()),
          <any>new Date(finishDate.format()),
          <any>this.timeFilterNumber > 0 ? this.timeFilterNumber : 1
        ));
    } else {
      return this.d3.histogram()
        .value((d: any) => { return d.date; })
        .domain(this.x.domain())
        .thresholds(<any>this.d3.timeHour.range(
          <any>new Date(startDate.format()),
          <any>new Date(finishDate.format()),
          <any>this.timeFilterNumber > 0 ? this.timeFilterNumber : 1
        ));
    }
  }

  private setHistogramTimeFrame(diff) {
    const barMinWidth = 25;
    const barsNumber = Math.round(this.width / barMinWidth);
    const hoursPerBar = (diff * 24) / barsNumber;
    const daysPerBar = diff / barsNumber;
    const weeksPerBar = (diff / 7) / barsNumber;
    const monthsPerBar = (diff / 30) / barsNumber;

    if (monthsPerBar > 1) {
      // months
      this.timeFilter = 'month';
      this.timeFilterNumber = Math.round(monthsPerBar);
    } else if (((diff / 7) / barsNumber) > 1) {
      // weeks
      this.timeFilter = '7d';
      this.timeFilterNumber = Math.round(weeksPerBar);
    } else if ((diff / barsNumber) >= 1) {
      // days
      this.timeFilter = 'd';
      this.timeFilterNumber = Math.round(daysPerBar);
    } else {
      // hours
      this.timeFilter = 'h';
      this.timeFilterNumber = Math.round(hoursPerBar);
    }
  }

  clearHistogram() {
    this.dateChangeEvent(this.minDateLimit, this.maxDateLimit);
  }

  updateDates(dates: any, updateLimits = false) {
    if (updateLimits) {
      this.minDateLimit = dates.start.clone();
    }
    if (updateLimits) {
      this.maxDateLimit = dates.end.clone();
    }
    this.modelStart = dates.start.clone();
    this.modelEnd = dates.end.clone();
  }

  // Event dataInformation
  dataInformationEvent(noData: boolean) {
    this.noData.emit(noData);
  }

  // Event totalDays
  totalDaysEvent(totalDays: number) {
    this.totalDays.emit(totalDays);
  }

  getStep(start, end) {
    if (!this.width || !this.margin || !this.height) { this.setHistogramSizes(); }
    try {
      const difference = end.diff(start, 'days');
      this.setHistogramTimeFrame(difference);
    } catch (err) {
      console.error('could not get step', err);
    }
    return this.timeFilterNumber > 0 ? this.timeFilterNumber + this.timeFilter : 1 + ' ' + this.timeFilter;
  }

  // Event dateChange
  dateChangeEvent(start, end) {
    if (!start && this.minDateLimit) {
      start = this.minDateLimit.clone();
    }
    if (!end && this.maxDateLimit) {
      end = this.maxDateLimit.clone();
    }
    const step = this.getStep(start, end);

    this.updateDates({start: start, end: end});

    this.loadingAnimation();

    this.dateChange.emit(<any>{
      start: start,
      end: end,
      step: step,
      from: 'histogram'
    });
  }

  loadingAnimation() {
    if (!this.height || !this.margin) { this.setHistogramSizes(); }
    this.d3.select('div#histogram').html('');

    this.svg = this.d3.select('div#histogram').append('svg')
        .attr('width', this.width + this.margin.left + this.margin.right)
        .attr('height', this.height + this.margin.top)
      .append('g')
        .attr('class', 'barsContainer')
        .attr('transform',
              'translate(' + this.margin.left + ',' + this.margin.top + ')');
    this.d3.select('div#histogram').append('div').attr('class', 'loadingWrapper');
    this.d3.select('.loadingWrapper').append('div').attr('class', 'loadingBar');
    this.d3.select('.loadingWrapper').append('div').attr('class', 'loadingText');

    this.d3.select('.loadingWrapper').attr('class', 'loadingWrapper loading').attr('style', `width: ${this.width}px`);
    this.d3.select('.loadingText').text('Loading histogram...');
    this.d3.select('.barsContainer').attr('class', 'barsContainer loading');
  }
}



// WEBPACK FOOTER //
// ./src/latitude-ui/timeslider/histogram/histogram.component.ts